import React, { useRef, useEffect, ReactNode, ComponentPropsWithoutRef } from "react";
import { Button, Icon } from "semantic-ui-react";

interface IModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  top?: number;
  right?: number;
  externalRef?: React.RefObject<HTMLDialogElement>;
  dialogProps?: ComponentPropsWithoutRef<"dialog">;
}

const Modal = ({ isOpen, onClose, children, top = 32, right = 16, externalRef, dialogProps }: IModalProps) => {
  const internalRef = useRef<HTMLDialogElement>(null);
  const dialogRef = externalRef ?? internalRef;
  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [isOpen]);

  // Close the modal if the click is outside the dialog content
  const handleClickOutside = (event: React.MouseEvent<HTMLElement>) => {
    if (dialogRef.current && event.target === dialogRef.current) {
      onClose();
    }
  };

  // Close modal on escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  return (
    <>
      {/* Disabling lint error to allow the modal background to be clicked */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <dialog
        ref={dialogRef}
        onClose={() => onClose()}
        className='tw-border-0 tw-bg-transparent tw-p-0'
        onClick={handleClickOutside}
        {...dialogProps}
      >
        <Button
          icon={<Icon name='close' color='grey' />}
          onClick={() => onClose()}
          className='icon-only-button'
          style={{ position: "absolute", top: top, right: right, zIndex: 1 }}
          aria-label='Close'
        />
        <div className='tw-relative'>{children}</div>
      </dialog>
    </>
  );
};

export default Modal;
